import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
// utilities
import { gaTag } from "@utils/browser-helpers";
import { setInputValue } from "@utils/input-helpers";
import { normalizingString } from "@utils/string-helpers";

const style = {
  textAreaStyle:
    "m-1 outline-none w-full min-h-[5.5rem] border-none",
  internalContainerStyle: "inline-flex",
};

const TextArea = ({
  dataTestID,
  inputName,
  className,
  textAreaStyle,
  inputWidth = "w-full",
  customGATag,
  defaultValue = "",
  textAreaContainerStyle="",
  onChangeCallback,
  onBlurCallback,
  onFocusCallback,
  inputValue = "",
  placeholderText,
  disabled = false,
  required,
  externalContainer = false,
  returnErrorStatus,
}) => {
  const [cursorPosition, setCursorPosition] = useState(0);
  const initRef = useRef();
  const preserveCursor = () =>
    initRef && initRef.current
      ? setCursorPosition(initRef.current.selectionStart)
      : null;

  const [value, setValue] = useState(defaultValue || inputValue);

  const [updatedPlaceholderText, setUpdatedPlaceholderText] =
    useState(placeholderText);
  useEffect(() => {
    setUpdatedPlaceholderText(placeholderText);
  }, [placeholderText]);
  useEffect(() => {
    function updateInputValue() {
      setValue(inputValue);
      returnErrorStatus(required ? !inputValue : false);
    }
    if (!onChangeCallback && value && inputValue !== value) {
      updateInputValue();
    }
    if (initRef) {
      initRef.current.setSelectionRange(cursorPosition, cursorPosition);
    }
  }, [inputValue]);

  const onChangeHandler = (e) => {
    preserveCursor();
    onChangeCallback ? onChangeCallback(e) : setValue(setInputValue(e));
  };
  const onBlurHandler = (e) =>
    onBlurCallback && e.hasOwnProperty("target")
      ? onBlurCallback({
          target: {
            name: e.target.name,
            value: normalizingString(e.target.value),
          },
        })
      : onBlurCallback
      ? onBlurCallback(e)
      : null;
  const onFocusHandler = (e) => (onFocusCallback ? onFocusCallback(e) : null);
  const inputId = `input__${inputName}`;
  const InputContents = () => (
    <div
      className={`rounded-[6px] items-center w-full h-full flex ${textAreaContainerStyle}  ${inputWidth}`}
    >
      <textarea
        id={inputId}
        data-ga_click_id={customGATag || `input: ${inputName}`}
        data-ga_tag={gaTag()}
        data-testid={dataTestID || inputId}
        className={`font-body text-opacity-87 text-xs-3 tracking-subhead leading-subhead ${inputName}-input-field ${className} ${textAreaStyle} ${style.textAreaStyle}`}
        onChange={(e) => onChangeHandler(e)}
        onBlur={(e) => onBlurHandler(e)}
        onFocus={(e) => onFocusHandler(e)}
        ref={initRef}
        placeholder={disabled ? "Disabled" : updatedPlaceholderText}
        name={`input-${inputName}`}
        value={onChangeCallback ? inputValue : value}
        disabled={disabled}
        required={required}
        title={updatedPlaceholderText}
      />
    </div>
  );

  return externalContainer ? (
    InputContents()
  ) : (
    <div className={style.internalContainerStyle}>{InputContents()}</div>
  );
};

TextArea.propTypes = {
  /** sets name to input field determines input id `input__${inputName}` and className tag `${inputName}-input-field*/
  inputName: PropTypes.string.isRequired,
  /** overwrites `input: ${inputName} */
  customGATag: PropTypes.string,
  /** bool to determine if parent container acts as wrapper for input items */
  externalContainer: PropTypes.bool,
  /** bool to determine if input is required */
  required: PropTypes.bool,
  /** custom input width set as "##rem" or other css size */
  inputWidth: PropTypes.string,
  /** optional function triggered when input changes */
  onChangeCallback: PropTypes.func,
  /** optional function triggered when user tabs out or clicks outside of field */
  onBlurCallback: PropTypes.func,
  /** optional function triggered when input is focused */
  onFocusCallback: PropTypes.func,
  /** default inputValue */
  inputValue: PropTypes.string,
  /** Container tailwind class style */
  textAreaContainerStyle: PropTypes.string,
  /** placeholder text, "Disabled" if disabled flag is true and disabledValue is null */
  placeholderText: PropTypes.string,
  /** disables input if true, changes placeholderText to "Disabled" if disabledValue is null */
  disabled: PropTypes.bool,
  /** function that retrieves and handles result of null input if required */
  returnErrorStatus: PropTypes.func,
};

export default TextArea;
